import type { PipelineAdminRunEvent, PipelineMessageResponse } from "@/lib/api/pipeline";

export type AuditProjectionStatus = "complete" | "partial" | "missing";

export type RecallInjectionAuditStepKey =
	| "selection"
	| "plan"
	| "recall"
	| "injection"
	| "final_usage"
	| "not_triggered";

export interface RecallInjectionAuditItem {
	key: RecallInjectionAuditStepKey;
	title: string;
	status: AuditProjectionStatus;
	statusLabel: string;
	summary: string;
	fields: string[];
	source: string;
}

export interface RecallInjectionAuditProjection {
	overallStatus: AuditProjectionStatus;
	coverageLabel: string;
	gapSummary: string;
	items: RecallInjectionAuditItem[];
}

export function buildRecallInjectionAuditProjection(
	result: PipelineMessageResponse | null,
	runEvents: PipelineAdminRunEvent[],
): RecallInjectionAuditProjection {
	const phase2 = phaseSummary(result, "phase2_recall");
	const phase3 = phaseSummary(result, "phase3_rerank");
	const phase4 = phaseSummary(result, "phase4_generate");
	const semanticEvent = eventByType(runEvents, "semantic_frame_completed");
	const playbookEvent = eventByType(runEvents, "playbook_recalled");
	const evidenceEvent = eventByType(runEvents, "evidence_fetch_started");
	const toolEvent = eventByType(runEvents, "tool_completed");
	const ragEvent = eventByType(runEvents, "rag_completed");
	const generationEvent = eventByType(runEvents, "generation_completed");
	const intentValue = firstText(
		eventField(eventByType(runEvents, "intent_completed"), "output", "intent"),
		eventField(eventByType(runEvents, "intent_completed"), "decision", "intent"),
		result?.intent.intent,
	);
	const semanticTask = firstText(
		eventField(semanticEvent, "output", "semantic_task"),
		eventField(semanticEvent, "decision", "semantic_task"),
		textField(phase2, "semantic_task"),
	);
	const playbookId = firstText(
		eventField(playbookEvent, "output", "playbook_id"),
		eventField(playbookEvent, "decision", "playbook_id"),
		result?.recommended_playbook?.id,
		textField(phase2, "playbook_id"),
	);
	const matchMode = firstText(
		eventField(playbookEvent, "output", "match_mode"),
		eventField(playbookEvent, "decision", "match_mode"),
		result?.match_mode,
		textField(phase2, "match_mode"),
	);
	const missingSlots = firstList(
		eventField(semanticEvent, "output", "missing_slots"),
		eventField(evidenceEvent, "output", "missing_slots"),
		phase2?.missing_slots,
		phase4?.missing_slots,
	);
	const notTriggered = firstRecordList(
		eventField(toolEvent, "output", "not_triggered"),
		eventField(evidenceEvent, "output", "not_triggered"),
		phase2?.not_triggered,
	);
	const hasNotTriggeredContract =
		result !== null &&
		(Array.isArray(eventField(toolEvent, "output", "not_triggered")) ||
			Array.isArray(eventField(evidenceEvent, "output", "not_triggered")) ||
			Array.isArray(phase2?.not_triggered));
	const evidenceNeeds = firstList(
		eventField(evidenceEvent, "output", "evidence_needs"),
		eventField(evidenceEvent, "decision", "evidence_needs"),
		phase2?.evidence_needs,
	);
	const recommendedSkills = result?.recommended_skills ?? [];
	const universalSkills = result?.universal_skills ?? [];
	const allowedSkills = firstList(phase2?.allowed_skills, [
		...recommendedSkills,
		...universalSkills,
	]);
	const skillResultCount = firstNumber(
		eventField(toolEvent, "output", "skill_result_count"),
		phase2?.skill_result_count,
		phase3?.skill_result_count,
	);
	const ragSnippetCount = firstNumber(
		eventField(ragEvent, "output", "rag_snippet_count"),
		phase2?.rag_snippet_count,
		phase3?.rag_snippet_count,
	);
	const evidenceRefs = firstList(
		generationEvent?.evidence_refs,
		eventField(generationEvent, "output", "evidence_refs"),
		phase4?.evidence_refs,
	);
	const recalledEvidenceCount = skillResultCount + ragSnippetCount;
	const evidenceNotUsed = result !== null && recalledEvidenceCount > 0 && evidenceRefs.length === 0;
	const promptSnapshot = asRecord(
		generationEvent?.raw.prompt_snapshot ?? asRecord(phase4?.prompt_snapshot),
	);
	const injectedVariables = asRecord(promptSnapshot?.injected_variables);
	const dPrimeContext = textField(injectedVariables, "d_prime_context");
	const items: RecallInjectionAuditItem[] = [
		{
			key: "selection",
			title: "选择结果",
			status: result ? (playbookId ? "complete" : "partial") : "missing",
			statusLabel: result ? (playbookId ? "可审计" : "未命中方案") : "待运行",
			summary: result
				? `意图 ${valueOrNone(intentValue)}；任务 ${valueOrNone(semanticTask)}；方案 ${valueOrNone(playbookId)}。`
				: "运行后展示意图、语义任务、方案命中和匹配方式。",
			fields: [
				`意图=${valueOrNone(intentValue)}`,
				`语义任务=${valueOrNone(semanticTask)}`,
				`方案=${valueOrNone(playbookId)}`,
				`匹配=${valueOrNone(matchMode)}`,
				`信息缺口=${listOrNone(missingSlots)}`,
			],
			source: "MessagesResponse.intent + phase2_recall + playbook_recalled",
		},
		{
			key: "plan",
			title: "能力计划",
			status: result
				? allowedSkills.length > 0 || evidenceNeeds.length > 0
					? "complete"
					: "partial"
				: "missing",
			statusLabel: result ? "已投影" : "待运行",
			summary: result
				? `计划能力 ${allowedSkills.length} 个；证据需求 ${evidenceNeeds.length} 项。`
				: "运行后展示方案推荐技能、通用技能和证据需求。",
			fields: [
				`方案技能=${listOrNone(recommendedSkills)}`,
				`通用技能=${listOrNone(universalSkills)}`,
				`允许能力=${listOrNone(allowedSkills)}`,
				`证据需求=${listOrNone(evidenceNeeds)}`,
			],
			source: "recommended_skills/universal_skills + evidence_fetch_started",
		},
		{
			key: "recall",
			title: "召回结果",
			status: result
				? skillResultCount > 0 || ragSnippetCount > 0 || evidenceNeeds.length === 0
					? "complete"
					: "partial"
				: "missing",
			statusLabel: result
				? skillResultCount > 0 || ragSnippetCount > 0
					? "已返回事实"
					: "待补事实"
				: "待运行",
			summary: result
				? `工具事实 ${skillResultCount} 条；知识片段 ${ragSnippetCount} 条。`
				: "运行后展示工具事实和知识片段是否返回。",
			fields: [
				`工具事实=${skillResultCount}`,
				`知识片段=${ragSnippetCount}`,
				`Shopify读取=${result?.runtime_capabilities.shopify_read_mode ?? "未运行"}`,
				`知识模式=${result?.runtime_capabilities.rag_mode ?? "未运行"}`,
			],
			source: "tool_completed + rag_completed + runtime_capabilities",
		},
		{
			key: "injection",
			title: "证据注入",
			status: result ? (promptSnapshot ? "complete" : "partial") : "missing",
			statusLabel: result ? (promptSnapshot ? "有快照" : "缺快照") : "待运行",
			summary: result
				? promptSnapshot
					? "已能看到主 Agent 生成时使用的提示词快照和注入变量。"
					: "当前只看到生成结果，提示词注入快照未稳定返回。"
				: "运行后展示主 Agent 生成前注入了哪些变量和证据。",
			fields: [
				`Prompt版本=${textField(promptSnapshot, "prompt_version") ?? "未返回"}`,
				`Prompt模式=${textField(promptSnapshot, "prompt_effect_mode") ?? result?.runtime_capabilities.prompt_effect_mode ?? "未运行"}`,
				`注入变量=${injectedVariables ? Object.keys(injectedVariables).join(",") || "无" : "未返回"}`,
				`d'注入=${dPrimeContext ? trimForDisplay(dPrimeContext) : "未返回"}`,
			],
			source: "generation_completed.raw.prompt_snapshot + phase4_generate",
		},
		{
			key: "final_usage",
			title: "最终使用",
			status: result
				? evidenceNotUsed
					? "partial"
					: result.final_outcome === "success"
						? "complete"
						: "partial"
				: "missing",
			statusLabel: result ? (evidenceNotUsed ? "证据未用上" : result.final_outcome) : "待运行",
			summary: result
				? evidenceNotUsed
					? `已召回 ${recalledEvidenceCount} 条工具/知识事实，但最终回复没有 evidence_refs，需要归因到注入、主 Agent 表达或证据质量。`
					: `对客动作 ${result.final_action_type}；最终结果 ${result.final_outcome}；使用证据 ${evidenceRefs.length} 条。`
				: "运行后展示最终回复是否真正使用了已召回证据。",
			fields: [
				`对客动作=${result?.final_action_type ?? "未运行"}`,
				`最终结果=${result?.final_outcome ?? "未运行"}`,
				`使用证据=${listOrNone(evidenceRefs)}`,
				`证据未使用=${evidenceNotUsed ? "evidence_injected_not_used" : "否"}`,
				`决策依据=${textField(phase4, "decision_basis") ?? "未返回"}`,
			],
			source: "phase4_generate + generation_completed + MessagesResponse.final_*",
		},
		{
			key: "not_triggered",
			title: "未触发原因",
			status: result ? (hasNotTriggeredContract ? "complete" : "partial") : "missing",
			statusLabel: result
				? hasNotTriggeredContract
					? notTriggered.length > 0
						? "已补原因"
						: "无跳过候选"
					: "待 S3D-2B"
				: "待运行",
			summary: result
				? hasNotTriggeredContract
					? notTriggered.length > 0
						? `已返回 ${notTriggered.length} 个候选未触发原因，可定位到缺槽位、配置或 handler。`
						: "本轮候选能力没有返回未触发原因，说明已计划能力均无显性跳过。"
					: "S3D-2A 只能基于已有事件判断数量、缺口和是否返回；具体候选为什么跳过，待 S3D-2B 增补 skipped_candidates/skip_reason。"
				: "运行后先展示显性缺口，后续再补候选未触发原因。",
			fields: hasNotTriggeredContract
				? [
						`显性缺口=${listOrNone(missingSlots)}`,
						`候选未触发=${notTriggered.length > 0 ? notTriggered.map(formatNotTriggered).join("；") : "无"}`,
						`证据需求=${listOrNone(evidenceNeeds)}`,
						`工具事实=${skillResultCount}`,
					]
				: [
						`显性缺口=${listOrNone(missingSlots)}`,
						`证据需求=${listOrNone(evidenceNeeds)}`,
						`工具事实=${skillResultCount}`,
						"S3D-2B=补候选未触发原因",
					],
			source: hasNotTriggeredContract
				? "phase2_recall.summary.not_triggered + tool_completed.output.not_triggered"
				: "phase2 missing_slots + S3D-2B 待补事件合同",
		},
	];
	return {
		overallStatus: overallStatus(items),
		coverageLabel: result
			? `已投影 ${items.filter((item) => item.status !== "missing").length}/${items.length} 项`
			: "待运行",
		gapSummary: hasNotTriggeredContract
			? "S3D-2B 已补最小候选未触发原因；后续归因链路和样本 expected_audit 继续在 S3E/S7 收口。"
			: "S3D-2A 不新增后端合同；缺失的候选召回、跳过原因和归因链路在 S3D-2B/S3D-2C 补齐。",
		items,
	};
}

function eventByType(
	events: PipelineAdminRunEvent[],
	eventType: string,
): PipelineAdminRunEvent | undefined {
	return events.find((event) => event.event_type === eventType);
}

function eventField(
	event: PipelineAdminRunEvent | undefined,
	bucket: "input" | "decision" | "output",
	key: string,
): unknown {
	return event?.[bucket]?.[key];
}

function phaseSummary(
	result: PipelineMessageResponse | null,
	phase: string,
): Record<string, unknown> | null {
	return result?.phase_trace.find((entry) => entry.phase === phase)?.summary ?? null;
}

function asRecord(value: unknown): Record<string, unknown> | null {
	if (value && typeof value === "object" && !Array.isArray(value)) {
		return value as Record<string, unknown>;
	}
	return null;
}

function textField(record: Record<string, unknown> | null | undefined, key: string): string | null {
	const value = record?.[key];
	if (value === null || value === undefined || value === "") return null;
	return String(value);
}

function firstText(...values: unknown[]): string | null {
	for (const value of values) {
		if (value === null || value === undefined || value === "") continue;
		return String(value);
	}
	return null;
}

function firstNumber(...values: unknown[]): number {
	for (const value of values) {
		if (typeof value === "number" && Number.isFinite(value)) return value;
		if (typeof value === "string" && value.trim() !== "") {
			const parsed = Number(value);
			if (Number.isFinite(parsed)) return parsed;
		}
	}
	return 0;
}

function firstList(...values: unknown[]): string[] {
	for (const value of values) {
		const list = toStringList(value);
		if (list.length > 0) return list;
	}
	return [];
}

function firstRecordList(...values: unknown[]): Record<string, unknown>[] {
	for (const value of values) {
		if (!Array.isArray(value)) continue;
		const records = value.filter(
			(item): item is Record<string, unknown> =>
				item !== null && typeof item === "object" && !Array.isArray(item),
		);
		if (records.length > 0 || value.length === 0) return records;
	}
	return [];
}

function toStringList(value: unknown): string[] {
	if (Array.isArray(value)) return value.map((item) => String(item)).filter(Boolean);
	if (typeof value === "string") {
		return value
			.split(",")
			.map((item) => item.trim())
			.filter(Boolean);
	}
	return [];
}

function valueOrNone(value: string | null): string {
	return value?.trim() ? value : "无";
}

function listOrNone(values: string[]): string {
	return values.length > 0 ? values.join(", ") : "无";
}

function formatNotTriggered(record: Record<string, unknown>): string {
	const skillId = textField(record, "skill_id") ?? "未知能力";
	const reason = textField(record, "reason") ?? "unknown_reason";
	const missing = firstList(record.missing_slots);
	const handler = textField(record, "handler_state");
	const detail = [
		missing.length > 0 ? `缺 ${missing.join("/")}` : null,
		handler ? `handler=${handler}` : null,
	].filter(Boolean);
	return `${skillId}:${reason}${detail.length > 0 ? `（${detail.join("，")}）` : ""}`;
}

function trimForDisplay(value: string): string {
	return value.length > 72 ? `${value.slice(0, 72)}...` : value;
}

function overallStatus(items: RecallInjectionAuditItem[]): AuditProjectionStatus {
	if (items.every((item) => item.status === "complete")) return "complete";
	if (items.some((item) => item.status !== "missing")) return "partial";
	return "missing";
}
